/**
 * Field normalization for partner school records.
 *
 * Every field arrives under three to five possible key names depending on the
 * endpoint, so that fallback logic lives here rather than in JSX.
 */

export interface NormalizedSchool {
  name: string
  logo: string
  accreditation: string
  /** Marketing paragraph. */
  about: string
  /** Partner-supplied HTML of selling points. */
  bodyHtml: string
  /** Short partner headline, used in the Clicks card's match slot. */
  headline: string
  /** The matched program name, the reason a Leads school is shown. */
  programName: string
  /** Short pill, e.g. "No application fees". */
  highlight: string
  clickUrl: string
  /** Partner-supplied TCPA HTML. */
  tcpaHtml: string
}

function pick(school: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const val = school[key]
    if (typeof val === 'string' && val.trim()) return val.trim()
    if (typeof val === 'number') return String(val)
  }
  return ''
}

export function normalizeSchool(
  school: Record<string, unknown>,
): NormalizedSchool {
  return {
    name: pick(school, [
      'mBrandName',
      'brandName',
      'schoolName',
      'mSchoolName',
      'name',
      'brand',
    ]),
    // Both the Leads and Clicks responses return the school logo as imageURL,
    // so it is checked first.
    logo: pick(school, [
      'imageURL',
      'imageUrl',
      'mImageURL',
      'image',
      'mBrandLogo',
      'brandLogo',
      'logo',
      'logoURL',
      'logoUrl',
      'mLogo',
    ]),
    accreditation: pick(school, [
      'mAccreditation',
      'accreditation',
      'accreditor',
      'mAccreditor',
    ]),
    about: pick(school, [
      'aboutBrand',
      'mBrandDescription',
      'brandDescription',
      'description',
      'blurb',
    ]),
    bodyHtml: pick(school, ['bodyContent', 'mBodyContent', 'body']),
    headline: pick(school, ['headContent', 'headline', 'mHeadContent']),
    programName: pick(school, [
      'mProgramName',
      'programName',
      'program',
      'mProgram',
    ]),
    highlight: pick(school, [
      'schoolHighlight',
      'mSchoolHighlight',
      'highlight',
      'mHighlight',
    ]),
    clickUrl: pick(school, ['clickURL', 'clickUrl', 'url', 'mClickURL']),
    tcpaHtml: pick(school, [
      'tcpaDisclaimer',
      'mTCPADisclaimer',
      'tcpaDisclosure',
      'mDisclosure',
      'disclosure',
      'consentText',
    ]),
  }
}
